import { AlertTitle, Box, SxProps } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import * as React from 'react';

import AlertErrorIcon from '../../../assets/icons/AlertError';
import AlertSuccessIcon from '../../../assets/icons/AlertSuccess';
import InfoFillIcon from '../../../assets/icons/InfoIconBackgroundFill';
import WarningRoundInfo from '../../../assets/icons/WarningRound';
import colors from '../../../constants/colors';
import { StatusType } from '../../../constants/enums';
import { AlertCloseButton } from '../IconButtons';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} {...props} />;
  }
);

interface StatusAlertProps {
  open: boolean;
  setOpen?: (open: boolean) => void;
  text?: React.ReactElement<any> | string;
  title?: React.ReactElement<any> | string;
  status: StatusType;
  borderColorVariant?: string;
  backgroundColorVariant?: string;
  fillVariant?: string;
  dataTestId?: string;
  className?: string | undefined;
  sx?: SxProps;
}

export const StatusAlert = ({
  open,
  setOpen,
  text,
  title,
  status,
  dataTestId,
  className,
  sx,
}: StatusAlertProps) => {
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen?.(false);
  };

  if (!open) return null;

  const statusVariants = {
    success: {
      borderColor: colors.csaaSpectrumGreen50,
      backgroundColor: colors.csaaLightGreen,
      icon: <AlertSuccessIcon sx={{ alignSelf: 'center' }} />,
    },
    info: {
      borderColor: colors.csaaSpectrumBlue50,
      backgroundColor: colors.csaaBlueInfo,
      icon: (
        <InfoFillIcon
          sx={{ alignSelf: 'center' }}
          fill={colors.csaaSpectrumBlue50}
        />
      ),
    },
    error: {
      borderColor: colors.csaaSpectrumRed50,
      backgroundColor: colors.csaaLightRed,
      icon: <AlertErrorIcon sx={{ alignSelf: 'center' }} />,
    },
    warning: {
      backgroundColor: '#FFFAE0',
      borderColor: '#EBA62F',
      icon: <WarningRoundInfo sx={{ alignSelf: 'center' }} />,
    },
  };

  const borderColor = statusVariants[status].borderColor;
  const backgroundColor = statusVariants[status].backgroundColor;
  const StatusIcon = statusVariants[status].icon;

  return (
    <Alert
      data-testid={dataTestId}
      onClose={setOpen ? handleClose : undefined}
      sx={{
        width: 1,
        minHeight: 64,
        border: 2,
        borderColor,
        borderRadius: 2,
        backgroundColor,
        color: 'text.primary',
        boxShadow: 'none',
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        fontSize: { mobile: 16, tablet: 18 },
        px: 3,
        '& .MuiAlert-message': {
          alignSelf: 'center',
        },
        ...sx,
      }}
      icon={StatusIcon}
      action={
        setOpen ? (
          <AlertCloseButton
            aria-label="close"
            baseColor={borderColor}
            sx={{
              alignSelf: 'center',
            }}
            data-testid="alertSuccessIconButton"
            onClick={handleClose}
          />
        ) : undefined
      }
    >
      {title && (
        <AlertTitle
          sx={{ fontWeight: 700, fontSize: { mobile: 16, tablet: 18 }, m: 0 }}
        >
          {title}
        </AlertTitle>
      )}
      <Box className={className}>{text}</Box>
    </Alert>
  );
};

export default StatusAlert;
